import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthFetch } from "../hooks/useAuthFetch";
import "../stili/MonitoraggioIscrizioni.css";
import "../stili/BottoniFinali.css";

const API_URL = process.env.REACT_APP_API_URL;

export default function MonitoraggioIscrizioni() {
  const navigate = useNavigate();
  const authFetch = useAuthFetch();

  const [stagioni, setStagioni] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [giocatori, setGiocatori] = useState([]);

  const [idStagione, setIdStagione] = useState("");
  const [idCategoria, setIdCategoria] = useState("");
  const [statoFiltro, setStatoFiltro] = useState("");
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const fetchLookup = useCallback(async () => {
    try {
      const [resStagioni, resCategorie] = await Promise.all([
        authFetch(`${API_URL}/api/admin/lookup/stagioni`),
        authFetch(`${API_URL}/api/admin/lookup/categorie`)
      ]);

      if (!resStagioni.ok) {
        throw new Error("Errore nel caricamento delle stagioni");
      }

      if (!resCategorie.ok) {
        throw new Error("Errore nel caricamento delle categorie");
      }

      const stagioniData = await resStagioni.json().catch(() => []);
      const categorieData = await resCategorie.json().catch(() => []);

      setStagioni(Array.isArray(stagioniData) ? stagioniData : []);
      setCategorie(Array.isArray(categorieData) ? categorieData : []);
    } catch (err) {
      console.error(err);
      setError("Impossibile caricare i filtri iniziali.");
      toast.error("Errore nel caricamento di stagioni e categorie");
    }
  }, [authFetch]);

  useEffect(() => {
    fetchLookup();
  }, [fetchLookup]);

  const handleSearch = async () => {
    setError("");
    setMessage("");

    if (!idStagione || !idCategoria) {
      setError("Seleziona stagione e categoria.");
      toast.warning("Seleziona stagione e categoria");
      return;
    }

    setLoading(true);

    try {
      const params = new URLSearchParams();
      params.append("idStagione", idStagione);
      params.append("idCategoria", idCategoria);

      if (statoFiltro) {
        params.append("stato", statoFiltro);
      }

      if (search.trim()) {
        params.append("search", search.trim());
      }

      const res = await authFetch(
        `${API_URL}/api/admin/iscrizioni/monitoraggio?${params.toString()}`
      );

      if (!res.ok) {
        throw new Error("Errore nel caricamento del monitoraggio iscrizioni");
      }

      const data = await res.json().catch(() => []);
      const lista = Array.isArray(data) ? data : [];

      setGiocatori(lista);
      setMessage(
        lista.length > 0
          ? `Trovati ${lista.length} giocatori.`
          : "Nessun giocatore trovato con i filtri selezionati."
      );
    } catch (err) {
      console.error(err);
      setGiocatori([]);
      setError("Errore durante il caricamento del monitoraggio.");
      toast.error("Errore durante il caricamento del monitoraggio");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setIdStagione("");
    setIdCategoria("");
    setStatoFiltro("");
    setSearch("");
    setGiocatori([]);
    setMessage("");
    setError("");
  };

  const handleDownloadPdf = async () => {
    if (!idStagione || !idCategoria) {
      setError("Seleziona stagione e categoria prima di stampare il PDF.");
      toast.warning("Seleziona stagione e categoria");
      return;
    }

    setDownloadLoading(true);
    setError("");
    setMessage("");

    try {
      const params = new URLSearchParams();
      params.append("idStagione", idStagione);
      params.append("idCategoria", idCategoria);

      const res = await authFetch(
        `${API_URL}/api/admin/iscrizioni/presenze/pdf?${params.toString()}`
      );

      if (!res.ok) {
        throw new Error("Errore nella generazione del PDF");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const categoriaObj = categorie.find((c) => String(c.id) === String(idCategoria));
      const stagioneObj = stagioni.find((s) => String(s.id) === String(idStagione));

      const categoriaNome = categoriaObj?.nome
        ? categoriaObj.nome.replace(/\s+/g, "_").toLowerCase()
        : "categoria";

      const stagioneNome = stagioneObj?.nome
        ? stagioneObj.nome.replace(/[^\w]+/g, "_").toLowerCase()
        : "stagione";

      const a = document.createElement("a");
      a.href = url;
      a.download = `foglio_presenze_${categoriaNome}_${stagioneNome}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);

      toast.success("PDF generato correttamente");
      setMessage("PDF foglio presenze scaricato correttamente.");
    } catch (err) {
      console.error(err);
      setError("Errore durante la generazione del PDF.");
      toast.error("Errore durante la generazione del PDF");
    } finally {
      setDownloadLoading(false);
    }
  };

  const riepilogo = useMemo(() => {
    const totale = giocatori.length;
    const attivi = giocatori.filter((g) => g.statoGiocatore === "ATTIVO").length;
    const inattivi = giocatori.filter((g) => g.statoGiocatore === "INATTIVO").length;
    const pagati = giocatori.filter((g) => g.statoPagamento === "PAGATO").length;
    const daVerificare = giocatori.filter((g) => g.statoPagamento !== "PAGATO").length;

    return {
      totale,
      attivi,
      inattivi,
      pagati,
      daVerificare
    };
  }, [giocatori]);

  const titoloGruppo = useMemo(() => {
    const categoriaObj = categorie.find((c) => String(c.id) === String(idCategoria));
    const stagioneObj = stagioni.find((s) => String(s.id) === String(idStagione));

    return {
      categoria: categoriaObj?.nome || "Gruppo squadra",
      stagione: stagioneObj?.nome || ""
    };
  }, [categorie, stagioni, idCategoria, idStagione]);

  return (
    <div className="monitoraggio-page">
      <div className="monitoraggio-shell">
        <div className="monitoraggio-container">
          <div className="monitoraggio-header">
            <h1>Monitoraggio Iscrizioni</h1>
            <p>Controlla i gruppi squadra e stampa il foglio presenze.</p>
          </div>

          <div className="monitoraggio-card filtri-card">
            <h2>Filtri</h2>

            <div className="monitoraggio-filtri-grid">
              <div className="monitoraggio-field">
                <label>Stagione</label>
                <select
                  value={idStagione}
                  onChange={(e) => setIdStagione(e.target.value)}
                >
                  <option value="">Seleziona stagione</option>
                  {stagioni.map((stagione) => (
                    <option key={stagione.id} value={stagione.id}>
                      {stagione.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="monitoraggio-field">
                <label>Categoria</label>
                <select
                  value={idCategoria}
                  onChange={(e) => setIdCategoria(e.target.value)}
                >
                  <option value="">Seleziona categoria</option>
                  {categorie.map((categoria) => (
                    <option key={categoria.id} value={categoria.id}>
                      {categoria.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="monitoraggio-field">
                <label>Stato giocatore</label>
                <select
                  value={statoFiltro}
                  onChange={(e) => setStatoFiltro(e.target.value)}
                >
                  <option value="">Tutti</option>
                  <option value="ATTIVO">ATTIVO</option>
                  <option value="INATTIVO">INATTIVO</option>
                </select>
              </div>

              <div className="monitoraggio-field">
                <label>Ricerca per cognome o nome</label>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Es. Rossi"
                />
              </div>
            </div>

            <div className="monitoraggio-actions">
              <button
                type="button"
                className="btn-monitoraggio primary"
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? "Caricamento..." : "Cerca"}
              </button>

              <button
                type="button"
                className="btn-monitoraggio secondary"
                onClick={handleReset}
              >
                Pulisci filtri
              </button>

              <button
                type="button"
                className="btn-monitoraggio success"
                onClick={handleDownloadPdf}
                disabled={downloadLoading}
              >
                {downloadLoading ? "Generazione PDF..." : "Stampa foglio presenze PDF"}
              </button>
            </div>
          </div>

          {(message || error) && (
            <div className={`monitoraggio-feedback ${error ? "error" : "success"}`}>
              {error || message}
            </div>
          )}

          <div className="monitoraggio-riepilogo">
            <div className="monitoraggio-stat-card">
              <span className="stat-label">Totale iscritti</span>
              <span className="stat-value">{riepilogo.totale}</span>
            </div>

            <div className="monitoraggio-stat-card">
              <span className="stat-label">Attivi</span>
              <span className="stat-value">{riepilogo.attivi}</span>
            </div>

            <div className="monitoraggio-stat-card">
              <span className="stat-label">Inattivi</span>
              <span className="stat-value">{riepilogo.inattivi}</span>
            </div>

            <div className="monitoraggio-stat-card">
              <span className="stat-label">Pagati</span>
              <span className="stat-value">{riepilogo.pagati}</span>
            </div>

            <div className="monitoraggio-stat-card">
              <span className="stat-label">Da verificare</span>
              <span className="stat-value">{riepilogo.daVerificare}</span>
            </div>
          </div>

          <div className="monitoraggio-card elenco-card">
            <div className="elenco-header">
              <div>
                <h2>{titoloGruppo.categoria}</h2>
                <p>{titoloGruppo.stagione ? `Stagione ${titoloGruppo.stagione}` : "Seleziona i filtri per iniziare"}</p>
              </div>
              <div className="elenco-badge">
                {giocatori.length} giocatori
              </div>
            </div>

            {giocatori.length === 0 ? (
              <div className="monitoraggio-empty">
                Nessun giocatore da mostrare.
              </div>
            ) : (
              <div className="giocatori-list">
                {giocatori.map((g) => (
                  <div key={g.idGiocatore} className="giocatore-card">
                    <div className="giocatore-main">
                      <h3>
                        {g.cognome} {g.nome}
                      </h3>
                      <div className="giocatore-meta">
                        <span>Data nascita: {g.dataNascita || "-"}</span>
                        <span>
                          Genitore: {[g.cognomeGenitore, g.nomeGenitore].filter(Boolean).join(" ") || "-"}
                        </span>
                        <span>Telefono: {g.telefonoGenitore || "-"}</span>
                      </div>
                    </div>

                    <div className="giocatore-side">
                      <span className={`badge stato ${String(g.statoGiocatore || "").toLowerCase()}`}>
                        {g.statoGiocatore || "N/D"}
                      </span>

                      <span className={`badge pagamento ${String(g.statoPagamento || "").toLowerCase().replace("_", "-")}`}>
                        {g.statoPagamento || "N/D"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-buttons-bottom">
            <button
              type="button"
              className="btn-indietro"
              onClick={() => navigate("/gestionale")}
            >
              Torna al gestionale
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}